import { useState, useCallback } from 'react';
import { Button, Card, Typography, notification } from 'antd';
import { UploadOutlined, FilePdfOutlined, FileWordOutlined, ArrowRightOutlined, DeleteFilled } from '@ant-design/icons';
import { useAuthUser } from 'react-auth-kit';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import mammoth from 'mammoth';
import { openNotification } from 'utils/notification';
import { END_POINT } from 'config/environment';
import { routePath } from 'config/routeMgt/routePath';
import { DashboardLayout } from 'components/layout/components/DashboardLayout';
import { Navbar } from "components/layout/components/Navbar";

const { Title, Text } = Typography;

export const ResumeUploadPage = () => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const authUser = useAuthUser();
    const navigate = useNavigate();
    const [parsedContent, setParsedContent] = useState<string>("");
    const [fileType, setFileType] = useState<'pdf' | 'docx' | null>(null);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        const isAcceptedType = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ].includes(file.type);

        if (!isAcceptedType) {
            openNotification({
                title: "Error",
                state: "error",
                description: "Only PDF or DOCX files are allowed!",
                duration: 8.0,
            });
            return;
        }

        setFile(file);
        handleFileParse(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
        },
        maxFiles: 1,
        multiple: false
    });

    const handleFileParse = async (file: File) => {
        const fileType = file.type;
        if (fileType === "application/pdf") {
            setFileType('pdf');
            const reader = new FileReader();
            reader.onload = async () => {
                const typedArray = new Uint8Array(reader.result as ArrayBuffer);
                const blob = new Blob([typedArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setParsedContent(url);
            };
            reader.readAsArrayBuffer(file);
        } else if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
            const reader = new FileReader();
            reader.onload = async () => {
                const result = await mammoth.extractRawText({ arrayBuffer: reader.result as ArrayBuffer });
                setParsedContent(result.value);
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('email', authUser()?.user.email);

        setLoading(true);
        try {
            const uploadRes = await fetch(`${END_POINT.AUTH_URL}/talent/process-cv`, {
                method: "POST",
                body: formData,
            });

            const result = await uploadRes.json();



            if (result.success) {
                openNotification({
                    title: "Success",
                    state: "success",
                    description: "Resume uploaded and analyzed successfully.",
                    duration: 4.5,
                });
                navigate(routePath.home);  
            } else {
                openNotification({
                    title: "Resume Analysis Error",
                    state: "error",
                    description: result.error || "Something went wrong while analyzing your resume.",
                    duration: 8.0,
                });
            }
            navigate(routePath.home);
        } catch (error) {
            console.log(error);
            openNotification({
                title: "Resume Analysis Error",
                state: "error",
                description: "Something went wrong while analyzing your resume.",
                duration: 8.0,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = () => {
        navigate(routePath.home);
    };

    return (
        <DashboardLayout>
            <Navbar title="Upload Resume" description="" />
            <div className="max-w-3xl mx-auto mt-8 px-4">
                <Card className="shadow-lg">
                    <Title level={3} className="text-center mb-6">Upload Your Resume</Title>

                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}
                    >
                        <input {...getInputProps()} />

                        <div className="space-y-4">
                            <UploadOutlined className="text-4xl text-gray-400" />

                            <div className="space-y-2">
                                <Text strong className="block text-lg">
                                    {isDragActive ? "Drop your file here" : "Drag & drop your resume"}
                                </Text>
                                <Text type="secondary">or click to browse</Text>
                            </div>

                            <div className="flex justify-center gap-2">
                                <FilePdfOutlined className="text-red-500" />
                                <FileWordOutlined className="text-blue-500" />
                                <Text type="secondary">(PDF, DOCX up to 10MB)</Text>
                            </div>
                        </div>
                    </div>

                    {file && (
                        <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {fileType === 'pdf' ? (
                                        <FilePdfOutlined className="text-red-500 text-xl" />
                                    ) : (
                                        <FileWordOutlined className="text-blue-500 text-xl" />
                                    )}
                                    <Text strong className="truncate">{file.name}</Text>
                                    <Text type="secondary" className="text-sm">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </Text>
                                </div>
                                <Button
                                    icon={<DeleteFilled />}
                                    type="text"
                                    danger
                                    onClick={() => setFile(null)}
                                />
                            </div>

                        </div>
                    )}

                    <div className="mt-6 flex justify-center gap-4">
                        <Button
                            type="default"
                            size="large"
                            onClick={handleSkip}
                            className="w-48 flex items-center justify-center"
                        >
                            Skip for now <ArrowRightOutlined className="ml-2" />
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            onClick={handleUpload}
                            loading={loading}
                            disabled={!file}
                            className="w-48"
                        >
                            {loading ? 'Processing...' : 'Upload Resume'}
                        </Button>
                    </div>
                </Card>
            </div>
        </DashboardLayout>
    );
};